import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  XCircle,
  Award,
  Clock,
  FileText
} from 'lucide-react';
import Navigation from '@/components/landing/Navigation';
import Footer from '@/components/landing/Footer';
import { testQuestions } from '@/mockData';

const TestRunner = () => {
  const { testId } = useParams();
  const navigate = useNavigate();
  
  const test = testQuestions[testId];
  
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState({});
  const [showResults, setShowResults] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  if (!test) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Card className="p-8 text-center max-w-md">
          <XCircle className="w-16 h-16 mx-auto mb-4 text-red-500" />
          <h2 className="text-2xl font-bold mb-2">Test Not Found</h2>
          <p className="text-gray-600 mb-4">The requested test could not be loaded.</p>
          <Button onClick={() => navigate('/resources')}>
            Back to Resources
          </Button>
        </Card>
      </div>
    );
  }

  const handleAnswerSelect = (answerIndex) => {
    setSelectedAnswers({
      ...selectedAnswers,
      [currentQuestion]: answerIndex
    });
  };

  const handleNext = () => {
    if (currentQuestion < test.questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    }
  };

  const handlePrevious = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };

  const handleSubmit = () => {
    setShowResults(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const calculateScore = () => {
    let correct = 0;
    test.questions.forEach((q, index) => {
      if (selectedAnswers[index] === q.correctAnswer) {
        correct++;
      }
    });
    return {
      correct,
      total: test.questions.length,
      percentage: Math.round((correct / test.questions.length) * 100)
    };
  };

  const score = calculateScore();
  const progress = ((currentQuestion + 1) / test.questions.length) * 100;
  const question = test.questions[currentQuestion];

  if (showResults) {
    return (
      <>
        <Navigation mobileMenuOpen={mobileMenuOpen} setMobileMenuOpen={setMobileMenuOpen} />
        
        <div className="min-h-screen bg-gray-50 pt-24 pb-12">
          <div className="max-w-4xl mx-auto px-6">
            {/* Results Header */}
            <Card className="p-8 mb-8 text-center" style={{ borderTop: '4px solid #006C69' }}>
              <Award className="w-20 h-20 mx-auto mb-4" style={{ color: '#B7BF72' }} />
              <h1 className="text-3xl font-bold mb-2">Test Complete!</h1>
              <p className="text-lg text-gray-600 mb-6">{test.title}</p>
              
              {/* Score Display */}
              <div className="grid grid-cols-3 gap-6 max-w-2xl mx-auto mb-8">
                <div className="bg-gray-50 rounded-lg p-6">
                  <div className="text-4xl font-bold" style={{ color: '#006C69' }}>
                    {score.percentage}%
                  </div>
                  <div className="text-sm text-gray-600 mt-1">Overall Score</div>
                </div>
                <div className="bg-gray-50 rounded-lg p-6">
                  <div className="text-4xl font-bold text-green-600">
                    {score.correct}
                  </div>
                  <div className="text-sm text-gray-600 mt-1">Correct</div>
                </div>
                <div className="bg-gray-50 rounded-lg p-6">
                  <div className="text-4xl font-bold text-red-600">
                    {score.total - score.correct}
                  </div>
                  <div className="text-sm text-gray-600 mt-1">Incorrect</div>
                </div>
              </div>

              {/* Performance Message */}
              <div className="mb-6">
                {score.percentage >= 80 && (
                  <Badge className="text-base px-4 py-2" style={{ background: '#006C69' }}>
                    🎉 Excellent Performance!
                  </Badge>
                )}
                {score.percentage >= 60 && score.percentage < 80 && (
                  <Badge className="text-base px-4 py-2" style={{ background: '#B7BF72', color: '#000' }}>
                    👍 Good Job! Keep Practicing
                  </Badge>
                )}
                {score.percentage < 60 && (
                  <Badge className="text-base px-4 py-2" variant="secondary">
                    📚 Review the material and try again
                  </Badge>
                )}
              </div>

              <div className="flex gap-4 justify-center">
                <Button
                  onClick={() => window.location.reload()}
                  className="font-semibold"
                  style={{ background: '#006C69' }}
                >
                  Retry Test
                </Button>
                <Button
                  onClick={() => navigate(`/resources/${test.category}`)}
                  variant="outline"
                  className="font-semibold"
                >
                  Back to Resources
                </Button>
              </div>
            </Card>

            {/* Answer Review */}
            <div className="space-y-4">
              <h2 className="text-2xl font-bold mb-4">Review Your Answers</h2>
              {test.questions.map((q, index) => {
                const userAnswer = selectedAnswers[index];
                const isCorrect = userAnswer === q.correctAnswer;
                
                return (
                  <Card key={q.id} className="p-6">
                    <div className="flex items-start gap-3 mb-3">
                      {isCorrect ? (
                        <CheckCircle2 className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                      ) : (
                        <XCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-1" />
                      )}
                      <div className="flex-1">
                        <div className="font-semibold text-gray-900 mb-2">
                          Question {index + 1}: {q.question}
                        </div>
                        
                        <div className="space-y-2 mb-3">
                          {q.options.map((option, optIndex) => {
                            const isUserAnswer = userAnswer === optIndex;
                            const isCorrectAnswer = q.correctAnswer === optIndex;
                            
                            let className = 'p-3 rounded-lg border ';
                            if (isCorrectAnswer) {
                              className += 'bg-green-50 border-green-300';
                            } else if (isUserAnswer && !isCorrect) {
                              className += 'bg-red-50 border-red-300';
                            } else {
                              className += 'bg-gray-50 border-gray-200';
                            }
                            
                            return (
                              <div key={optIndex} className={className}>
                                <div className="flex items-center gap-2">
                                  <span className="font-medium text-sm">
                                    {String.fromCharCode(65 + optIndex)}.
                                  </span>
                                  <span className="text-sm">{option}</span>
                                  {isCorrectAnswer && (
                                    <Badge className="ml-auto" style={{ background: '#006C69' }}>
                                      Correct
                                    </Badge>
                                  )}
                                  {isUserAnswer && !isCorrect && (
                                    <Badge className="ml-auto" variant="destructive">
                                      Your Answer
                                    </Badge>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                        
                        {q.explanation && (
                          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                            <div className="text-sm font-semibold text-blue-900 mb-1">
                              Explanation:
                            </div>
                            <div className="text-sm text-blue-800">{q.explanation}</div>
                          </div>
                        )}
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>
          </div>
        </div>
        
        <Footer />
      </>
    );
  }

  return (
    <>
      <Navigation mobileMenuOpen={mobileMenuOpen} setMobileMenuOpen={setMobileMenuOpen} />
      
      <div className="min-h-screen bg-gray-50 pt-24 pb-12">
        <div className="max-w-4xl mx-auto px-6">
          {/* Test Header */}
          <div className="mb-6">
            <Button
              variant="ghost"
              onClick={() => navigate(`/resources/${test.category}`)}
              className="mb-4"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Resources
            </Button>
            
            <h1 className="text-3xl font-bold mb-2">{test.title}</h1>
            <div className="flex flex-wrap gap-3">
              <Badge style={{ background: '#006C69' }}>
                <FileText className="w-3 h-3 mr-1" />
                {test.totalQuestions} Questions
              </Badge>
              <Badge style={{ background: '#B7BF72', color: '#000' }}>
                {test.difficulty}
              </Badge>
              {test.duration && (
                <Badge variant="outline">
                  <Clock className="w-3 h-3 mr-1" />
                  {test.duration}
                </Badge>
              )}
            </div>
          </div>

          {/* Progress Bar */}
          <Card className="p-6 mb-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700">
                Question {currentQuestion + 1} of {test.questions.length}
              </span>
              <span className="text-sm font-medium" style={{ color: '#006C69' }}>
                {Math.round(progress)}% Complete
              </span>
            </div>
            <Progress value={progress} className="h-2" />
          </Card>

          {/* Question Card */}
          <Card className="p-8 mb-6">
            <div className="mb-6">
              <div className="text-sm font-semibold text-gray-500 mb-2">
                QUESTION {currentQuestion + 1}
              </div>
              <h2 className="text-xl font-bold text-gray-900">{question.question}</h2>
            </div>

            <div className="space-y-3">
              {question.options.map((option, index) => {
                const isSelected = selectedAnswers[currentQuestion] === index;
                
                return (
                  <button
                    key={index}
                    onClick={() => handleAnswerSelect(index)}
                    className={`w-full text-left p-4 rounded-lg border-2 transition-all ${
                      isSelected
                        ? 'border-[#006C69] bg-[#006C69]/5'
                        : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                          isSelected
                            ? 'border-[#006C69] bg-[#006C69]'
                            : 'border-gray-300'
                        }`}
                      >
                        {isSelected && (
                          <CheckCircle2 className="w-4 h-4 text-white" />
                        )}
                      </div>
                      <div className="flex-1">
                        <span className="font-medium mr-2">
                          {String.fromCharCode(65 + index)}.
                        </span>
                        <span className="text-gray-900">{option}</span>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </Card>

          {/* Navigation Buttons */}
          <div className="flex items-center justify-between">
            <Button
              onClick={handlePrevious}
              disabled={currentQuestion === 0}
              variant="outline"
              className="font-semibold"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Previous
            </Button>

            <div className="text-sm text-gray-600">
              {Object.keys(selectedAnswers).length} / {test.questions.length} answered
            </div>

            {currentQuestion < test.questions.length - 1 ? (
              <Button
                onClick={handleNext}
                className="font-semibold"
                style={{ background: '#006C69' }}
              >
                Next
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            ) : (
              <Button
                onClick={handleSubmit}
                className="font-semibold"
                style={{ background: '#B7BF72', color: '#000' }}
                disabled={Object.keys(selectedAnswers).length < test.questions.length}
              >
                Submit Test
                <CheckCircle2 className="w-4 h-4 ml-2" />
              </Button>
            )}
          </div>

          {/* Warning if not all answered */}
          {currentQuestion === test.questions.length - 1 &&
            Object.keys(selectedAnswers).length < test.questions.length && (
              <Card className="mt-4 p-4 bg-amber-50 border-amber-200">
                <p className="text-sm text-amber-800">
                  ⚠️ You have unanswered questions. Please answer all questions before submitting.
                </p>
              </Card>
            )}
        </div>
      </div>
      
      <Footer />
    </>
  );
};

export default TestRunner;
